import type {
  Channel,
  LastReply,
  Maybe,
  CommentData,
} from '~publish/gql/graphql'

import { CommentAvatar } from './CommentAvatar'
import { CommentItem } from '../CommentItem'
import { CommentHeader } from '../components/CommentHeader'
import { CommentText } from '../components/CommentText'
import { CommentContextProvider } from '../context/CommentContext'

type LastAuthorReplyProps = {
  channel: Channel
  reply?: Maybe<LastReply> | undefined
}

export const LastAuthorReply = ({
  channel,
  reply,
}: LastAuthorReplyProps): JSX.Element | null => {
  if (!reply) {
    return null
  }

  // Generate stable ID for semantic grouping
  const replyId = `reply-${channel.id}-${reply.author.username}`
    .replace(/\s+/g, '-')
    .toLowerCase()

  // Use type assertion since LastReply has the core fields needed by context components
  const replyAsComment = reply as unknown as CommentData

  return (
    <CommentContextProvider comment={replyAsComment}>
      <CommentItem
        as="div"
        role="complementary"
        aria-label="Author's reply"
        aria-labelledby={`${replyId}-header`}
        variant="lastReply"
        avatar={
          <CommentAvatar
            hasParent={true}
            avatar={reply.isOwnReply ? channel.avatar : reply.author.avatar}
            username={reply.author.username ?? 'username'}
            showDot={false}
            showConnector={false}
          />
        }
      >
        <CommentHeader />
        <CommentText />
      </CommentItem>
    </CommentContextProvider>
  )
}
